import React, { useState } from 'react';
import { RotateCcwIcon, SaveIcon, AlertTriangleIcon } from 'lucide-react';
import { pricingOpenItems, type PricingConfig } from '../../data/pricingConfig';
import { packingCatalog } from '../../data/packingCatalog';
import { loadPricingConfig, resetPricingConfig, savePricingConfig } from '../../utils/pricingStore';
function NumberInput({
  label,
  value,
  onChange,
  step = 0.25,
  prefix
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  step?: number;
  prefix?: string;
}) {
  return <label className="block" data-id="element-265" data-mp="2hc4x7zdcytwj">
      <span className="mb-1 block text-xs font-semibold text-ink-600" data-id="element-266" data-mp="2hbvgl2e7dz3i">{label}</span>
      <span className="flex h-11 items-center rounded-lg border-2 border-ink-200 bg-white px-3 focus-within:border-ink" data-id="element-267" data-mp="2cism8s1lqlm9">
        {prefix && <span className="mr-1 text-ink-500" data-id="element-268" data-mp="27jfz066tjcj5">{prefix}</span>}
        <input type="number" step={step} value={value} onChange={e => onChange(Number(e.target.value))} className="w-full bg-transparent text-sm font-semibold tabular-nums focus:outline-none" data-id="element-269" data-mp="351g1hqtx36ff" />
      </span>
    </label>;
}
export function PricingPanel() {
  const [cfg, setCfg] = useState<PricingConfig>(() => loadPricingConfig());
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const set = (patch: Partial<PricingConfig>) => setCfg(c => ({
    ...c,
    ...patch
  }));
  const example = cfg.rates[2] * (cfg.minimumServiceHours + cfg.travelHours);
  return <div className="space-y-10" data-id="element-270" data-mp="3h9v8z7afjica">
      <section className="flex flex-wrap items-center justify-between gap-4" data-id="element-271" data-mp="3h8jvugv3gy8i">
        <p className="font-mono text-sm text-ink-500" data-id="element-272" data-mp="3lcrim8hzs443">{cfg.version}</p>
        <div className="flex gap-2" data-id="element-273" data-mp="37c74hpn0uwgx">
          <button type="button" onClick={() => {
          setCfg(resetPricingConfig());
          setSavedAt(null);
        }} className="inline-flex h-11 items-center gap-2 rounded-lg border-2 border-ink-200 px-4 text-sm font-semibold hover:border-ink" data-id="element-274" data-mp="37d8myf6dal8o">
            <RotateCcwIcon className="h-4 w-4" data-id="element-275" data-mp="15ihgul7h2a6r" /> Reset
          </button>
          <button type="button" onClick={() => {
          setCfg(savePricingConfig(cfg));
          setSavedAt(new Date().toLocaleTimeString());
        }} className="inline-flex h-11 items-center gap-2 rounded-lg bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-700" data-id="element-276" data-mp="1aj57h5rsgp0g">
            <SaveIcon className="h-4 w-4" data-id="element-277" data-mp="z425r7ptp3ra" /> Save version (preview only)
          </button>
        </div>
      </section>
      {savedAt && <p role="status" className="-mt-6 text-sm text-lime-dark" data-id="element-278" data-mp="u6afei4ttpp7">Saved at {savedAt}. Reload the funnel to use it. Production config must be published on the server.</p>}

      <section className="grid gap-6 lg:grid-cols-2" data-id="element-279" data-mp="1mu6bplqhhflg">
        <div data-id="element-280" data-mp="1muofd3fh6w3e">
          <h3 className="font-display text-lg font-extrabold" data-id="element-281" data-mp="1mvzh6xpd1trv">Approved standard rates</h3>
          <div className="mt-4 grid grid-cols-2 gap-3" data-id="element-282" data-mp="1i1d2olnzfl3k">
            <NumberInput label="2 movers + 1 truck /h" prefix="$" step={1} value={cfg.rates[2]} onChange={v => set({
            rates: {
              ...cfg.rates,
              2: v
            }
          })} data-id="element-283" data-mp="1d4v3s2fc2gz5" />
            <NumberInput label="3 movers + 1 truck /h" prefix="$" step={1} value={cfg.rates[3]} onChange={v => set({
            rates: {
              ...cfg.rates,
              3: v
            }
          })} data-id="element-284" data-mp="2albqdxkou1u6" />
            <NumberInput label="Minimum service h" step={0.5} value={cfg.minimumServiceHours} onChange={v => set({
            minimumServiceHours: v
          })} data-id="element-285" data-mp="2e2g4yor0exv0" />
            <NumberInput label="Travel h (billed)" step={0.5} value={cfg.travelHours} onChange={v => set({
            travelHours: v
          })} data-id="element-286" data-mp="2i4tbvghht1td" />
            <NumberInput label="Operating surcharge %" step={1} value={cfg.surchargePercent} onChange={v => set({
            surchargePercent: v
          })} data-id="element-287" data-mp="2s1pe54gssrph" />
            <NumberInput label="Deposit" prefix="$" step={1} value={cfg.depositAmount} onChange={v => set({
            depositAmount: v
          })} data-id="element-288" data-mp="2wzr3u88qbp9x" />
          </div>
          <p className="mt-3 text-sm text-ink-600" data-id="element-289" data-mp="35sud04ccdfyr">
            Formula before surcharge/tax/card: rate × (max(service h, {cfg.minimumServiceHours}) + {cfg.travelHours}). Minimum example, 2 movers: <strong data-id="element-290" data-mp="3fqqrn64hmt1m">${example}</strong> before surcharge and extras.
          </p>
        </div>
        <div data-id="element-291" data-mp="3kpuwxloak6mo">
          <h3 className="font-display text-lg font-extrabold" data-id="element-292" data-mp="3kplgaop4zbtn">Negotiation floors (server-authorized, never shown to customers)</h3>
          <div className="mt-4 grid grid-cols-2 gap-3" data-id="element-293" data-mp="lasuhtllorzs">
            <NumberInput label="2 movers floor" prefix="$" step={1} value={cfg.negotiationFloors[2]} onChange={v => set({
            negotiationFloors: {
              ...cfg.negotiationFloors,
              2: v
            }
          })} data-id="element-294" data-mp="gcr9320pcgl5" />
            <NumberInput label="3 movers floor" prefix="$" step={1} value={cfg.negotiationFloors[3]} onChange={v => set({
            negotiationFloors: {
              ...cfg.negotiationFloors,
              3: v
            }
          })} data-id="element-295" data-mp="v3go6fhaafua" />
          </div>
          <p className="mt-3 text-sm text-ink-600" data-id="element-296" data-mp="2et8czfb0io4">2 movers: $119 → $109 → lowest $99 for eligible easy, prepared jobs. 2-bedroom upstairs generally stays at $129. Must be recorded with a reason in the accepted estimate. No automatic discounts in the public funnel.</p>
        </div>
      </section>

      <section className="rounded-xl border border-ink-300 bg-white p-5" data-id="element-297" data-mp="7eflz9z1o0xg">
        <h3 className="flex items-center gap-2 font-semibold text-ink" data-id="element-298" data-mp="1mr8p1wjn0kgc">
          <AlertTriangleIcon className="h-4 w-4 text-magenta" data-id="element-299" data-mp="1iq6nh928zwb7" /> Open items blocking final totals
        </h3>
        <ul className="mt-3 space-y-2 pl-5 text-sm text-ink-700" data-id="element-300" data-mp="1q9y1onbhfhjs">
          {pricingOpenItems.map(c => <li key={c} className="list-disc" data-id="element-301" data-mp="1vbf2kityv40d">{c}</li>)}
        </ul>
      </section>

      <section data-id="element-302" data-mp="1gduyrf8t3evs">
        <h3 className="font-display text-lg font-extrabold" data-id="element-303" data-mp="1gee6620tykvk">Planning hours (estimates only)</h3>
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7" data-id="element-304" data-mp="1lbwkk7xm9say">
          {(['studio', '1', '2', '3', '4', '5', 'few'] as const).map(b => <NumberInput key={b} label={`${b === 'studio' ? 'Studio' : b === 'few' ? 'Few items' : b === '5' ? '5+ BR' : `${b} BR`} h`} value={cfg.baseHoursByBedroom[b]} onChange={v => set({
          baseHoursByBedroom: {
            ...cfg.baseHoursByBedroom,
            [b]: v
          }
        })} step={0.5} data-id="element-305" data-mp="2e0lf1o462zdq" />)}
          <NumberInput label="2nd-floor stairs +h" value={cfg.stairsUpliftHours.second} onChange={v => set({
          stairsUpliftHours: {
            ...cfg.stairsUpliftHours,
            second: v
          }
        })} data-id="element-306" data-mp="2s0cttr2icjss" />
          <NumberInput label="3rd-floor stairs +h" value={cfg.stairsUpliftHours.third} onChange={v => set({
          stairsUpliftHours: {
            ...cfg.stairsUpliftHours,
            third: v
          }
        })} data-id="element-307" data-mp="2wsnz79g0oxsq" />
          <NumberInput label="Elevator allowance h" value={cfg.elevatorAllowanceHours} onChange={v => set({
          elevatorAllowanceHours: v
        })} data-id="element-308" data-mp="31oi6zre97j6a" />
          <NumberInput label="Range spread h" value={cfg.rangeSpreadHours} onChange={v => set({
          rangeSpreadHours: v
        })} step={0.5} data-id="element-309" data-mp="2snake6l3o6s1" />
        </div>
      </section>

      <section data-id="element-310" data-mp="2np8up2t6597l">
        <h3 className="font-display text-lg font-extrabold" data-id="element-311" data-mp="2npsd0kmqdzm5">Packing & materials catalog</h3>
        <div className="mt-4 overflow-x-auto rounded-xl border border-ink-200 bg-white" data-id="element-312" data-mp="2ivealy9nis7z">
          <table className="w-full min-w-[560px] text-left text-sm" data-id="element-313" data-mp="2dw1ojfqltp6g">
            <thead className="border-b border-ink-200 bg-ink-100 text-xs font-semibold text-ink-600" data-id="element-314" data-mp="29xu70iobgju7">
              <tr data-id="element-315" data-mp="24w4wr58x4a76">
                <th className="px-4 py-2.5" data-id="element-316" data-mp="24wobkd39uigz">Item</th>
                <th className="px-4 py-2.5" data-id="element-317" data-mp="1zy3bqerxm0t2">Type</th>
                <th className="px-4 py-2.5" data-id="element-318" data-mp="200g2nhfo3li9">Labor incl.</th>
                <th className="px-4 py-2.5" data-id="element-319" data-mp="ff9czc49dygi">Approved price</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200" data-id="element-320" data-mp="kecj6zvz6aq2">
              {packingCatalog.map(p => <tr key={p.id} data-id="element-321" data-mp="pftgymj5xh2f">
                  <td className="px-4 py-3 font-semibold" data-id="element-322" data-mp="pcdxz8mdbnvq">{p.label.en}</td>
                  <td className="px-4 py-3" data-id="element-323" data-mp="tfk2obtu1703">{p.kind}</td>
                  <td className="px-4 py-3" data-id="element-324" data-mp="3svgyf04wa3rd">{p.includesLabor ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-3" data-id="element-325" data-mp="1oxgz24jzfk3">{p.price !== null ? `$${p.price}` : 'Not approved — “priced by our team”'}</td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}